import { Container, Group, Stack } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import {
  getCoreRowModel,
  PaginationState,
  useReactTable,
} from "@tanstack/react-table"
import { Button, ErrorBox } from "@tfg/ui"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { FindBatchUpdateOperationsResponseDto } from "@tfg/types/schemas/modules/traceability/queries/find-batch-update-operations.response.dto"

import { queryClient } from "~/backend/queryClient.ts"
import { Table } from "~/components/table/Table.tsx"
import { useBatchUpdateModalContext } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/BatchModal/store/BatchModalContext.tsx"
import { useBatchColumns } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/batch.columns.tsx"
import { ProductionSiteDisplay } from "~/pages/SupplierPortal/TraceabilityRequests/components/ProductionSiteDisplay.tsx"
import { useFindOperationsWithEmptyFields } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useFindOperationsWithEmptyFields.ts"
import { useUpdateOperationsMutation } from "~/pages/SupplierPortal/TraceabilityRequests/hooks.ts"

type Props = {
  batchOperations: FindBatchUpdateOperationsResponseDto
  checkList: number[]
  setCheckList: Dispatch<SetStateAction<number[]>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  setIsLoading: Dispatch<SetStateAction<boolean>>
}

export function BatchUpdateOperationsTable({
  batchOperations,
  checkList,
  setCheckList,
  pagination,
  setPagination,
  setIsLoading,
}: Props) {
  const { setBatchModalOpen, batchModalInfo } = useBatchUpdateModalContext()
  const [pageCount, setPageCount] = useState<number>(0)
  const { t } = useTranslation(["traceability"])

  const {
    mutate: updateOperations,
    isLoading: isUpdating,
    error,
  } = useUpdateOperationsMutation(batchModalInfo?.requestId ?? 0, {
    onSuccess: async (data, variables) => {
      setBatchModalOpen(false)
      setCheckList([])
      if (data.informationRequestIds) {
        await Promise.all(
          data.informationRequestIds.map(async (id) => {
            await queryClient.invalidateQueries([
              "listTraceabilityRequests",
              id,
            ])
          })
        )
      } else {
        await queryClient.invalidateQueries(["listTraceabilityRequests"])
      }

      notifications.show({
        title: t("batchModal.batchUpdateSuccess"),
        message: t("batchModal.batchUpdateSuccessMessage", {
          count: variables.ids.length,
        }),
        color: "green",
      })
    },
  })
  useEffect(() => {
    setPageCount(Math.ceil(batchOperations.count / pagination.pageSize))
  }, [batchOperations, pagination.pageSize])

  useEffect(() => {
    setIsLoading(isUpdating)
  }, [isUpdating, setIsLoading])

  const columns = useBatchColumns({
    batchOperations: batchOperations.operations,
    key: batchModalInfo?.key ?? "",
    checkList,
    setCheckList,
  }).filter(Boolean)

  const table = useReactTable({
    data: batchOperations.operations,
    columns,
    state: {
      pagination,
    },
    pageCount,
    manualPagination: true,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
  })

  const { refetch: refetchEmptyOperations } = useFindOperationsWithEmptyFields(
    batchOperations.operationsIdList,
    batchModalInfo?.key ?? "",
    batchModalInfo && batchModalInfo.update.customFieldsValues !== undefined
      ? batchModalInfo?.update.customFieldsValues[0]?.customFieldDefinitionId
      : undefined
  )
  if (error) {
    return <ErrorBox error={error} />
  }
  if (!batchModalInfo) {
    return null
  }
  return (
    <Stack>
      <Group>
        <Button
          variant="secondary"
          size="compact-sm"
          onClick={() => setCheckList(batchOperations.operationsIdList)}
        >
          {t("selectAll")}
        </Button>
        <Button
          variant="secondary"
          size="compact-sm"
          onClick={() => setCheckList([])}
        >
          {t("deselectAll")}
        </Button>
        <Button
          variant="secondary"
          size="compact-sm"
          onClick={async () => {
            const result = await refetchEmptyOperations()
            if (result?.data) {
              setCheckList(result.data)
            }
          }}
        >
          {t("batchModal.actions.selectEmpty")}
        </Button>
      </Group>
      <Container mah="80%" m={0} maw="100%">
        <Table
          table={table}
          pageIndex={pagination.pageIndex}
          manualPaginationCount={batchOperations.count}
        />
      </Container>
      <Group
        justify="center"
        h="10vh"
        pos="sticky"
        bottom={0}
        bg="white"
        w="100%"
        style={{
          borderTop: "1px solid var(--mantine-color-gray-2)",
        }}
      >
        {batchModalInfo.update.productionSiteId && (
          <ProductionSiteDisplay
            productionSiteId={batchModalInfo.update.productionSiteId}
          />
        )}
        <Button
          disabled={isUpdating}
          onClick={() => {
            updateOperations({
              ids: checkList,
              update: batchModalInfo.update,
            })
          }}
        >
          {t("batchModal.applyButton", {
            type: batchModalInfo.label,
            itemCount: checkList.length,
          })}
        </Button>
      </Group>
    </Stack>
  )
}
